import _extends from "@babel/runtime/helpers/esm/extends";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import CrossIcon from "@dcl/icons/esm/ui/cross";
import Heading, { types } from "../../elements/Heading";
import React from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import getClassNameFactory from "../../utilities/classNames";
import { Provider as IsModalOpenProvider } from "./isOpenContext";
import { Rank } from "../../core/types";

var getFocusableDescendants = function getFocusableDescendants(node) {
  /*
   * this is based on the code provided by:
   * https://www.w3.org/TR/wai-aria-practices-1.1/examples/dialog-modal/dialog.html
   * at `aria.Utils.isFocusable`
   */
  var selector = ["button:not([disabled])", "[href]", "input:not([disabled])", "select:not([disabled])", "textarea:not([disabled])", "[tabindex]:not([tabindex='-1'])"].join(", ");
  return node.querySelectorAll(selector);
};

var getModalRoot = function getModalRoot() {
  var modalRootId = "modalRoot";
  var modalRoot = document.getElementById(modalRootId);

  if (!modalRoot) {
    var newElement = document.createElement("div");
    newElement.id = modalRootId;
    document.body.appendChild(newElement);
    return newElement;
  }

  return modalRoot;
};

var Modal = /*#__PURE__*/function (_React$Component) {
  _inherits(Modal, _React$Component);

  var _super = _createSuper(Modal);

  function Modal() {
    var _this;

    _classCallCheck(this, Modal);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty(_assertThisInitialized(_this), "getClassName", getClassNameFactory(Modal.displayName));

    _defineProperty(_assertThisInitialized(_this), "contentRef", /*#__PURE__*/React.createRef());

    _defineProperty(_assertThisInitialized(_this), "dialogRef", /*#__PURE__*/React.createRef());

    _defineProperty(_assertThisInitialized(_this), "closeRef", /*#__PURE__*/React.createRef());

    _defineProperty(_assertThisInitialized(_this), "appRoot", document.getElementById(_this.props.appRootId || "root"));

    _defineProperty(_assertThisInitialized(_this), "modalRoot", getModalRoot());

    _defineProperty(_assertThisInitialized(_this), "el", document.createElement("div"));

    _defineProperty(_assertThisInitialized(_this), "lastScrollRatio", void 0);

    _defineProperty(_assertThisInitialized(_this), "lastFocusEl", void 0);

    _defineProperty(_assertThisInitialized(_this), "ref", void 0);

    _defineProperty(_assertThisInitialized(_this), "activeElementBeforeOpenModal", document.activeElement);

    _defineProperty(_assertThisInitialized(_this), "handleClose", function (event) {
      if (event) {
        event.stopPropagation();
      }

      _this.props.closeThisComponent();
    });

    _defineProperty(_assertThisInitialized(_this), "handleKeydown", function (event) {
      /** TODO: these should be provided by another module/typings file, enum or const object yet to be determined */
      var ESCAPE_KEY_CODE = 27;
      var ESCAPE = "escape"; // event.keyCode is deprecated, we should use event.key

      if (event.keyCode === ESCAPE_KEY_CODE || event.key === ESCAPE) {
        _this.handleClose();
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleOverlayClick", function (event) {
      // close if target element is the overlay, not the dialog
      if (event.target === _this.ref.current) {
        _this.handleClose(event);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleFocus", function (event) {
      if (!_this.dialogRef.current) {
        return;
      }

      var isTargetWithinModal = _this.dialogRef.current.contains(event.target);

      if (isTargetWithinModal) {
        _this.lastFocusEl = {
          current: event.target
        };
        return;
      }

      var focusableDialogDescendants = getFocusableDescendants(_this.dialogRef.current);

      if (!focusableDialogDescendants.length && _this.contentRef.current) {
        _this.contentRef.current.focus();

        return;
      }

      if (_this.lastFocusEl.current === focusableDialogDescendants[0]) {
        focusableDialogDescendants[focusableDialogDescendants.length - 1].focus();
      } else {
        focusableDialogDescendants[0].focus();
      }

      _this.lastFocusEl = {
        current: document.activeElement
      };
    });

    return _this;
  }

  _createClass(Modal, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var _this$props;

      /*
       * The portal element is inserted in the DOM tree after
       * the Modal's children are mounted, meaning that children
       * will be mounted on a detached DOM node. If a child
       * component requires to be attached to the DOM tree
       * immediately when mounted, for example to measure a
       * DOM node, or uses 'autoFocus' in a descendant, add
       * state to Modal and only render the children when Modal
       * is inserted in the DOM tree.
       */
      this.modalRoot.appendChild(this.el);
      /**
       * hide all other page content from screen readers
       * https://www.w3.org/TR/wai-aria-1.0/states_and_properties#aria-hidden
       */

      this.appRoot.setAttribute("aria-hidden", "true");
      var scrollTop = window.scrollY || window.pageYOffset || 0;
      this.lastScrollRatio = scrollTop / document.body.scrollHeight;

      if (!((_this$props = this.props) !== null && _this$props !== void 0 && _this$props.disableScrollLocked)) {
        document.body.classList.add("is-scrollLocked");
      }

      document.addEventListener("focus", this.handleFocus, true);

      if (!this.props.uncloseable) {
        document.addEventListener("keydown", this.handleKeydown, false);
      }

      this.lastFocusEl = this.contentRef;

      if (this.contentRef.current) {
        this.contentRef.current.focus();
      } // According to the accessibility standarts, when Modal is open, the close icon should be focused by default


      if (this.closeRef.current && !this.props.isCloseButtonHidden) {
        this.closeRef.current.focus();
      } // close all other modals, and add this instance to the list


      Modal.mountedModals.forEach(function (modal) {
        return modal.handleClose();
      });
      Modal.mountedModals.push(this);
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      var _this2 = this;

      this.modalRoot.removeChild(this.el);
      this.appRoot.removeAttribute("aria-hidden");
      document.body.classList.remove("is-scrollLocked");
      document.removeEventListener("focus", this.handleFocus, true);

      if (!this.props.uncloseable) {
        document.removeEventListener("keydown", this.handleKeydown, false);
      }

      var scrollTop = this.lastScrollRatio * document.body.scrollHeight;
      window.scroll(0, scrollTop); // remove this instance from the list of mounted modals

      Modal.mountedModals = Modal.mountedModals.filter(function (modalInstance) {
        return modalInstance !== _this2;
      }); // Return focus to the last active element before the Modal was opened

      if (this.activeElementBeforeOpenModal) {
        this.activeElementBeforeOpenModal.focus();
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;

      /*
       * Bracket the dialog node with two invisible, focusable nodes.
       * While this dialog is open, we use these to make sure that focus never
       * leaves the document even if dialogNode is the first or last node.
       *
       * following example of:
       * https://www.w3.org/TR/wai-aria-practices-1.1/examples/dialog-modal/dialog.html
       * see: `aria.Dialog` constructor
       */
      var a11yPropsContainer = {};

      if (this.props.ariaLabel && !this.props.headerId) {
        a11yPropsContainer = {
          "aria-label": this.props.ariaLabel
        };
      } else if (this.props.headerId) {
        a11yPropsContainer["aria-labelledby"] = this.props.headerId;
        a11yPropsContainer["aria-describedby"] = "".concat(this.props.headerId, "-dialogBody");
      }

      var a11yPropsCloseButton = {};

      if (this.props.closeButtonAriaLabel) {
        a11yPropsCloseButton["aria-label"] = this.props.closeButtonAriaLabel;
      }

      var dialogBodyId = this.props.headerId ? {
        id: "".concat(this.props.headerId, "-dialogBody")
      } : {};
      return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(IsModalOpenProvider, {
        value: true
      }, /*#__PURE__*/React.createElement("div", {
        className: classNames(this.getClassName(), this.props.className) // eslint-disable-next-line
        ,
        onClick: this.props.uncloseable ? undefined : this.handleOverlayClick,
        ref: function ref(_ref) {
          if (_ref) {
            _this3.ref = {
              current: _ref
            };
          }
        }
      }, /*#__PURE__*/React.createElement("div", _extends({}, a11yPropsContainer, {
        "aria-modal": true,
        className: classNames(this.getClassName({
          descendantName: "dialog",
          modifiers: this.props.animation
        })),
        role: "dialog",
        ref: this.dialogRef
      }), !this.props.uncloseable && /*#__PURE__*/React.createElement("div", {
        className: this.getClassName({
          descendantName: "header"
        })
      }, /*#__PURE__*/React.createElement("div", {
        className: this.getClassName({
          descendantName: "headerOuter"
        })
      }, this.props.headerTitle && /*#__PURE__*/React.createElement("div", {
        className: this.getClassName({
          descendantName: "headerTitle"
        })
      }, /*#__PURE__*/React.createElement("div", {
        className: this.getClassName({
          descendantName: "headerTitleCenter"
        })
      }, /*#__PURE__*/React.createElement(Heading, {
        id: "Title",
        type: types.Small,
        rank: Rank.Second
      }, typeof this.props.headerTitle === "string" ? /*#__PURE__*/React.createElement("strong", null, this.props.headerTitle) : this.props.headerTitle))), !this.props.isCloseButtonHidden && /*#__PURE__*/React.createElement("div", {
        className: this.getClassName({
          descendantName: "headerInner"
        })
      }, /*#__PURE__*/React.createElement("button", _extends({}, a11yPropsCloseButton, {
        className: this.getClassName({
          descendantName: "button"
        }),
        onClick: this.handleClose,
        ref: this.closeRef
      }), this.props.closeLabel && /*#__PURE__*/React.createElement("span", {
        className: this.getClassName({
          descendantName: "buttonLabel",
          utilities: "typographySmallCaps"
        })
      }, this.props.closeLabel), /*#__PURE__*/React.createElement("span", {
        className: this.getClassName({
          descendantName: "buttonCross"
        })
      }, /*#__PURE__*/React.createElement(CrossIcon, null)))))), /*#__PURE__*/React.createElement("div", _extends({
        className: this.getClassName({
          descendantName: "content"
        }),
        ref: this.contentRef
      }, dialogBodyId), /*#__PURE__*/React.createElement("div", {
        className: this.getClassName({
          descendantName: "contentOuter"
        })
      }, /*#__PURE__*/React.createElement("div", {
        className: this.getClassName({
          descendantName: "contentInner"
        })
      }, this.props.children)))), /*#__PURE__*/React.createElement("div", {
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex: 0
      }))), this.el);
    }
  }]);

  return Modal;
}(React.Component);

_defineProperty(Modal, "displayName", "Modal");

_defineProperty(Modal, "mountedModals", []);

export default Modal;